// Altarchiv-Übernahme (#517): Schalter, Erklärung und Fortschritt für Dokumente → Altarchiv.
// Vorgabe Patrick: jede Automatik wird erklärt und ist ein-/ausschaltbar. Ändern dürfen nur Admins (RLS
// auf app_settings, Präfix 'altarchiv_'). Der Hintergrundlauf selbst steckt in der Edge Function
// 'altarchiv-uebernahme'; sie liest denselben Schlüssel (siehe Test).
import { supabase } from '../supabase/client';

export const ALTARCHIV_KEY = 'altarchiv_funktionen';

export const ALTARCHIV_ERKLAERUNG = {
  titel: 'Altarchiv ins Google Drive übernehmen',
  was: 'Die App liest jedes PDF aus dem alten OneDrive (vorhandener Text, sonst Texterkennung) und schlägt vor: neuen Namen nach dem Schema Datum_Objektnummer_Art_Beschreibung_Absender, den Zielordner im Unterordner „Altarchiv“ des Objekts, betroffene Einheit, Mieter, Eigentümer oder Dienstleister und wie sicher der Vorschlag ist. Die Erkennung läuft über Mistral (EU). Sie arbeitet im Hintergrund paketweise, bis alle PDFs geprüft sind; ausschalten hält sie nach dem laufenden Paket an.',
  nicht: 'Legt nichts im Google Drive ab, benennt nichts um und verschiebt nichts – es entstehen nur Vorschläge zum Prüfen. Das Original im OneDrive bleibt unverändert. Doppelte Dateien werden nicht geprüft.',
};

export interface LaufSchalter { lauf: boolean; pro_lauf: number; modell: string | null }

// Nur ausdrücklich true ist an; Paketgröße 1–50, Vorgabe 20. modell: Mistral-Modell-ID für den Lauf
// (z. B. "mistral-small-2603"); fehlt es, gilt das Mistral-Modell aus den KI-Einstellungen.
export function leseLaufSchalter(wert: unknown): LaufSchalter {
  const o = wert && typeof wert === 'object' && !Array.isArray(wert) ? wert as Record<string, unknown> : {};
  const n = Number(o.pro_lauf);
  const m = typeof o.modell === 'string' && /^[a-z0-9.-]{3,60}$/.test(o.modell.trim()) ? o.modell.trim() : null;
  return { lauf: o.lauf === true, pro_lauf: Number.isFinite(n) && n >= 1 ? Math.min(50, Math.floor(n)) : 20, modell: m };
}

export const MASSEN_KEY = 'altarchiv_massenablage';

export const MASSEN_ERKLAERUNG = {
  titel: 'Sichere Vorschläge automatisch übernehmen',
  was: 'Übernimmt im Hintergrund alle sehr sicheren und eher sicheren Vorschläge mit Objekt, Zielordner und Datum ins Google Drive (Objekt › Altarchiv › …), Objekt für Objekt. Die Seite muss dafür nicht offen bleiben; ausschalten hält nach dem laufenden Paket an.',
  nicht: 'Unsichere Vorschläge, Dateien ohne Datum oder ohne Objekt bleiben zur Einzelprüfung. Das Original im OneDrive bleibt unverändert.',
};

export interface MassenSchalter { lauf: boolean; ab: number }

export function leseMassenSchalter(wert: unknown): MassenSchalter {
  const o = wert && typeof wert === 'object' && !Array.isArray(wert) ? wert as Record<string, unknown> : {};
  const ab = Number(o.ab);
  return { lauf: o.lauf === true, ab: Number.isFinite(ab) && ab >= 0.7 ? ab : 0.9 };
}

export async function ladeMassenSchalter(): Promise<MassenSchalter> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', MASSEN_KEY).maybeSingle();
  if (error) throw error;
  return leseMassenSchalter(data?.value);
}

/** Speichert den Schalter; beim Einschalten wird die Massenablage sofort angestoßen. */
export async function setzeMassenablage(an: boolean, alt: MassenSchalter): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert(
    { key: MASSEN_KEY, value: { ...alt, lauf: an }, updated_at: new Date().toISOString() },
    { onConflict: 'key' },
  );
  if (error) throw error;
  if (an) {
    const { error: e } = await supabase.functions.invoke('altarchiv-uebernahme', { body: { aktion: 'massenablage' } });
    if (e) throw e;
  }
}

export interface AltarchivStatistik {
  status: Partial<Record<'neu' | 'in_arbeit' | 'vorschlag' | 'in_ablage' | 'ohne_objekt' | 'ausgeschlossen' | 'duplikat' | 'fehler' | 'abgelegt' | 'abgelehnt', number>>;
  sicherheit: { hoch: number; mittel: number; niedrig: number };
  ablage_bereit?: number;
  ablage_fehler?: number;
  zuletzt: string | null;
}

export interface Fortschritt { gesamt: number; geprueft: number; offen: number; prozent: number }

/** Duplikate zählen nicht mit; „in Arbeit“ gilt als offen. */
export function berechneFortschritt(s: AltarchivStatistik): Fortschritt {
  const st = s.status;
  const offen = (st.neu ?? 0) + (st.in_arbeit ?? 0);
  const geprueft = (st.vorschlag ?? 0) + (st.ohne_objekt ?? 0) + (st.ausgeschlossen ?? 0) + (st.fehler ?? 0) + (st.abgelegt ?? 0) + (st.abgelehnt ?? 0);
  const gesamt = offen + geprueft;
  return { gesamt, geprueft, offen, prozent: gesamt ? Math.floor((geprueft / gesamt) * 100) : 0 };
}

export async function ladeLaufSchalter(): Promise<LaufSchalter> {
  const { data, error } = await supabase.from('app_settings').select('value').eq('key', ALTARCHIV_KEY).maybeSingle();
  if (error) throw error;
  return leseLaufSchalter(data?.value);
}

/** Speichert den Schalter; beim Einschalten wird der Lauf sofort angestoßen (danach läuft er selbst weiter). */
export async function setzeLauf(an: boolean, alt: LaufSchalter): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert(
    { key: ALTARCHIV_KEY, value: { ...alt, lauf: an }, updated_at: new Date().toISOString() },
    { onConflict: 'key' },
  );
  if (error) throw error;
  if (an) {
    const { error: e } = await supabase.functions.invoke('altarchiv-uebernahme', { body: { aktion: 'lauf' } });
    if (e) throw e;
  }
}

export async function ladeStatistik(): Promise<AltarchivStatistik> {
  const { data, error } = await supabase.rpc('altarchiv_statistik');
  if (error) throw error;
  return data as AltarchivStatistik;
}

const ADMIN_ROLLEN = ['admin', 'super admin', 'master admin'];

export async function darfAltarchivSteuern(): Promise<boolean> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return false;
  const { data } = await supabase.from('profiles').select('role').eq('id', user.id).maybeSingle();
  return ADMIN_ROLLEN.includes(((data?.role as string) || '').toLowerCase());
}
